import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Building2, TrendingUp, Users } from "lucide-react";
import { motion } from "framer-motion";

export const Services = () => {
  const services = [
    {
      icon: Building2,
      title: "Full Property Management",
      subtitle: "For Property Owners",
      description:
        "Complete end-to-end management including tenant screening, rent collection, maintenance coordination, and legal compliance for Johnson County properties.",
      features: [
        "Tenant screening & placement",
        "Rent collection & accounting",
        "Property inspections",
        "Legal compliance",
      ],
    },
    {
      icon: TrendingUp,
      title: "Renovation & Maintenance",
      subtitle: "For Investors",
      description:
        "Professional renovation services and 24/7 maintenance support to maximize property value and minimize vacancy periods in Overland Park and surrounding areas.",
      features: [
        "Emergency repairs",
        "Preventive maintenance",
        "Renovation planning",
        "Vendor network",
      ],
    },
    {
      icon: Users,
      title: "Tenant Services",
      subtitle: "For Renters",
      description:
        "Streamlined rental process with online applications, maintenance requests, and responsive customer service for quality renters in Leawood, Olathe, Lenexa, and Shawnee.",
      features: [
        "Online rental applications",
        "Digital lease signing",
        "Maintenance portal",
        "24/7 support",
      ],
    },
  ];

  return (
    <section id="services" className="py-14 sm:py-18 lg:py-22 bg-secondary/20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
            Everything You Need to Succeed
          </h2>
          <p className="text-lg md:text-xl text-foreground/80 max-w-3xl mx-auto font-medium">
            Full-service property management for owners, investors, and quality tenants across Johnson County, Kansas
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ delay: index * 0.15, duration: 0.5 }}
              >
                <Card
                  data-testid="services-card"
                  className="group h-full shadow-[0_10px_30px_rgba(15,42,74,0.06)] hover:shadow-[0_16px_45px_rgba(15,42,74,0.10)] transition-all"
                >
                  <CardHeader>
                    <div className="h-12 w-12 rounded-xl bg-accent/10 text-accent flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="text-xs font-medium text-accent mb-2">
                      {service.subtitle}
                    </div>
                    <CardTitle className="font-display text-xl">
                      {service.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {service.description}
                    </p>
                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="text-sm text-foreground/80 flex items-start"
                        >
                          <span className="mr-2 text-accent">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                    <Button
                      variant="link"
                      className="p-0 h-auto text-accent"
                      data-testid="services-learn-more-link"
                    >
                      Learn More →
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
